import { accountStore } from '../lib/studyStorage';

export interface HistoryLesson {
  id: string;
  title: string;
  subject: string;
  subjectId: string;
  topicId: string;
  topicName: string;
  grade: '10' | '11' | '12';
  thumbnail: string;
  duration: number;
  watchedSeconds: number;
  completionPercent: number;
  lastWatchedAt: string;
  createdAt: string;
}

export interface HistoryQuiz {
  id: string;
  title: string;
  subject: string;
  score: number;
  total: number;
  completedAt: string;
}

export interface HistoryTopic {
  id: string;
  name: string;
  subject: string;
  grade: '10' | '11' | '12';
  openedAt: string;
}

export interface HistoryState {
  lessons: HistoryLesson[];
  quizzes: HistoryQuiz[];
  topics: HistoryTopic[];
  savedLessonIds: string[];
}

const store = accountStore<HistoryState>('history', () => ({
  lessons: [], quizzes: [], topics: [], savedLessonIds: [],
}));

function loadFromStorage(): HistoryState {
  return store.read();
}

function saveToStorage(state: HistoryState) {
  store.write(state);
}


function dedupeById<T extends { id: string }>(items: T[], max: number): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
      if (result.length >= max) break;
    }
  }
  return result;
}

export function recordLessonWatch(data: Omit<HistoryLesson, 'watchedSeconds' | 'completionPercent' | 'lastWatchedAt' | 'createdAt'> & { watchedSeconds: number }) {
  const state = loadFromStorage();
  const existing = state.lessons.findIndex((l) => l.id === data.id);
  const now = new Date().toISOString();
  const prevSeconds = existing >= 0 ? state.lessons[existing].watchedSeconds : 0;
  const newSeconds = prevSeconds + data.watchedSeconds;
  const completionPercent = data.duration > 0 ? Math.min(100, Math.round((newSeconds / data.duration) * 100)) : 0;

  const record: HistoryLesson = {
    ...data,
    watchedSeconds: newSeconds,
    completionPercent,
    lastWatchedAt: now,
    createdAt: existing >= 0 ? state.lessons[existing].createdAt : now,
  };

  if (existing >= 0) {
    state.lessons.splice(existing, 1);
  }
  state.lessons.unshift(record);
  state.lessons = dedupeById(state.lessons, 100);
  saveToStorage(state);
}

export function recordTopicOpen(data: Omit<HistoryTopic, 'openedAt'>) {
  const state = loadFromStorage();
  const now = new Date().toISOString();
  const existing = state.topics.findIndex((t) => t.id === data.id);
  if (existing >= 0) state.topics.splice(existing, 1);
  state.topics.unshift({ ...data, openedAt: now });
  state.topics = dedupeById(state.topics, 50);
  saveToStorage(state);
}

export function recordQuizComplete(data: Omit<HistoryQuiz, 'completedAt'>) {
  const state = loadFromStorage();
  const now = new Date().toISOString();
  state.quizzes.unshift({ ...data, completedAt: now });
  state.quizzes = dedupeById(state.quizzes, 50);
  saveToStorage(state);
}

export function toggleSavedLesson(lessonId: string): boolean {
  const state = loadFromStorage();
  const idx = state.savedLessonIds.indexOf(lessonId);
  if (idx >= 0) {
    state.savedLessonIds.splice(idx, 1);
    saveToStorage(state);
    return false;
  }
  state.savedLessonIds.unshift(lessonId);
  if (state.savedLessonIds.length > 100) state.savedLessonIds.pop();
  saveToStorage(state);
  return true;
}

export function isLessonSaved(lessonId: string): boolean {
  const state = loadFromStorage();
  return state.savedLessonIds.includes(lessonId);
}

export function getHistoryState(): HistoryState {
  const state = loadFromStorage();
  return { ...state, lessons: [...state.lessons], quizzes: [...state.quizzes], topics: [...state.topics], savedLessonIds: [...state.savedLessonIds] };
}

export function getRecentActivity(limit: number = 6): Array<{ type: 'lesson' | 'quiz' | 'topic'; title: string; subject: string; time: string; icon: string; color: string; accent: string }> {
  const state = loadFromStorage();
  const items: Array<{ ts: string; type: 'lesson' | 'quiz' | 'topic'; title: string; subject: string; icon: string; color: string; accent: string }> = [];

  for (const l of state.lessons.slice(0, limit)) {
    items.push({
      ts: l.lastWatchedAt,
      type: 'lesson',
      title: l.title,
      subject: l.subject,
      icon: 'ri-video-line',
      color: '#C0392B',
      accent: '',
    });
  }
  for (const t of state.topics.slice(0, limit)) {
    items.push({
      ts: t.openedAt,
      type: 'topic',
      title: t.name,
      subject: t.subject,
      icon: 'ri-book-2-line',
      color: '#2980B9',
      accent: '',
    });
  }
  for (const q of state.quizzes.slice(0, limit)) {
    items.push({
      ts: q.completedAt,
      type: 'quiz',
      title: q.title,
      subject: q.subject,
      icon: 'ri-questionnaire-line',
      color: '#27AE60',
      accent: '',
    });
  }

  items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  const subjectColors: Record<string, string> = {
    'Mathematics': '#C0392B',
    'Physical Sciences': '#E67E22',
    'Life Sciences': '#27AE60',
    'Accounting': '#8E44AD',
    'CAT': '#2980B9',
  };

  return items.slice(0, limit).map((item) => ({
    ...item,
    color: subjectColors[item.subject] || '#6E6E73',
    accent: `${subjectColors[item.subject] || '#6E6E73'}15`,
    time: formatRelativeTime(item.ts),
  }));
}

export function getSavedLessonIds(): string[] {
  const state = loadFromStorage();
  return [...state.savedLessonIds];
}

export function clearHistory() {
  const state = { lessons: [], quizzes: [], topics: [], savedLessonIds: [] };
  saveToStorage(state);
}

function formatRelativeTime(iso: string): string {
  const now = new Date();
  const then = new Date(iso);
  const diff = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

const subjectColorMap: Record<string, string> = {
  'Mathematics': '#C0392B',
  'Physical Sciences': '#E67E22',
  'Life Sciences': '#27AE60',
  'Accounting': '#8E44AD',
  'CAT': '#2980B9',
};

export function getDailyGoals() {
  const state = loadFromStorage();
  const now = new Date();
  const todayKey = now.toISOString().split('T')[0];
  const todayLessons = state.lessons.filter((l) => l.lastWatchedAt.startsWith(todayKey));
  const todayQuizzes = state.quizzes.filter((q) => q.completedAt.startsWith(todayKey));
  const todayMinutes = todayLessons.reduce((sum, l) => sum + Math.floor(l.watchedSeconds / 60), 0);

  const goalMinutes = 30;
  const goalLessons = 2;
  const goalQuizzes = 1;

  return {
    streak: Math.min(state.lessons.length + state.quizzes.length, 14),
    minutesWatched: todayMinutes,
    minutesGoal: goalMinutes,
    lessonsCompleted: todayLessons.length,
    lessonsGoal: goalLessons,
    quizzesCompleted: todayQuizzes.length,
    quizzesGoal: goalQuizzes,
    progressPercent: Math.min(100, Math.round((todayMinutes / goalMinutes) * 100)),
    todayKey,
  };
}

export interface Recommendation {
  id: string;
  type: 'continue' | 'next_lesson' | 'topic' | 'subject_review';
  title: string;
  subtitle: string;
  subject: string;
  subjectId: string;
  icon: string;
  color: string;
  action: 'lesson' | 'topic' | 'subject';
  actionId: string;
}

export function getRecommendations(): Recommendation[] {
  return [];
}
